import { create } from 'zustand';

import { MediaControls } from '../utils/media-controls';

type MediaElement = HTMLVideoElement | HTMLAudioElement;

type BonusFeedMediaStoreState = {
	activeElement: MediaElement | null;
	playGeneration: number;
	elementsInViewport: MediaElement[];
	modalBonusId: string | null;
	isModalOpen: boolean;
	registerElement: (element: MediaElement) => void;
	unregisterElement: (element: MediaElement) => void;
	playVideo: (element: MediaElement) => Promise<void>;
	pauseVideo: (element: MediaElement) => void;
	getViewportElements: () => MediaElement[];
	getActiveElement: () => MediaElement | null;
	setModalOpen: (isOpen: boolean, bonusId?: string) => void;
	hasPriorityVideo: (bonusId?: string) => boolean;
};

const validateElement = (element: MediaElement): boolean => {
	if (!MediaControls.validateMediaElement(element)) {
		console.warn('Only video and audio media elements are supported');
		return false;
	}
	return true;
};

export const useGlobalMediaStore = create<BonusFeedMediaStoreState>((set, get) => {
	const validateRegistration = (element: MediaElement): boolean => {
		if (!get().elementsInViewport.includes(element)) {
			console.warn('Cannot operate on an unregistered media element');
			return false;
		}
		return true;
	};

	return {
		activeElement: null,
		playGeneration: 0,
		elementsInViewport: [],
		modalBonusId: null,
		isModalOpen: false,

		// Register just adds it to viewport list but doens't play it
		registerElement: (newElement) => {
			if (!validateElement(newElement)) return;

			set((state) => {
				const elements = [...state.elementsInViewport];
				if (!elements.includes(newElement)) {
					elements.push(newElement);
				}
				return { elementsInViewport: elements };
			});
		},

		// Unregister and pause if it's the active element
		unregisterElement: (element) => {
			// Skip if element isn't registered and paused
			if (!get().elementsInViewport.includes(element) && element.paused) {
				return;
			}
			// If this is the active element, pause it before unregistering
			const current = get().activeElement;
			if (current === element && !element.paused) {
				MediaControls.pause(element, () => {
					set((state) => ({
						activeElement: null,
						elementsInViewport: state.elementsInViewport.filter((item) => item !== element),
					}));
				});
			} else {
				set((state) => {
					const elements = state.elementsInViewport.filter((item) => item !== element);
					return { elementsInViewport: elements };
				});
			}
		},

		playVideo: async (element) => {
			if (!validateElement(element) || !validateRegistration(element)) {
				return;
			}

			const current = get().activeElement;

			if (current && current !== element) {
				MediaControls.pause(current);
			}

			// Protect against overlapping play requests (double-clicks, orchestration + user click, etc).
			const nextGeneration = get().playGeneration + 1;
			set({ activeElement: element, playGeneration: nextGeneration });

			const didPlay = await MediaControls.play(element);

			// If another play request happened while we were awaiting, ignore this result.
			if (get().playGeneration !== nextGeneration) return;

			// If playback failed (including timeout), roll back the active element so we
			// don't claim an "active" media element that isn't actually playing.
			if (!didPlay) {
				if (get().activeElement === element) {
					set({ activeElement: null });
				}
			}
		},

		pauseVideo: (element) => {
			if (!validateElement(element)) return;
			const current = get().activeElement;
			if (current === element) {
				MediaControls.pause(element, () => {
					set({ activeElement: null });
				});
			}
		},

		getViewportElements: () => {
			return get().elementsInViewport;
		},
		getActiveElement: () => {
			return get().activeElement;
		},

		setModalOpen: (isOpen, bonusId) => {
			set({
				isModalOpen: isOpen,
				modalBonusId: isOpen && bonusId ? bonusId : null,
			});
		},

		hasPriorityVideo: (bonusId) => {
			// A video has priority if it matches the current modal bonus ID
			return get().isModalOpen && bonusId !== undefined && bonusId === get().modalBonusId;
		},
	};
});
